import { COVERAGE, GAP_TONE, INK } from "../../../../theme/tokens";

/**
 * How a Missing cell reads, ranked inside its OWN shift row.
 *
 * Two tiers, exactly as specified: the highest missing value in the row is red, the second highest is
 * yellow, and everything else is neutral. Only shortfalls are graded — a surplus or an exactly-balanced
 * cell is never coloured, because the grid's job is to point at where people are MISSING, and colouring
 * a surplus (which the previous percentile ranking did) sent the eye to the opposite of the problem.
 */
export type GapTone = "critical" | "short" | "neutral";

/**
 * Every colour a tone can be drawn in, in one object.
 *
 * `bg` + `ink` + `bar` dress a SURFACE that carries text (a grid cell, a legend chip) and are
 * therefore contrast-bound — `ink` is the accessible dark amber / dark red, never the tier's
 * saturated colour. `marker` dresses a solid DOT with no text on it (the legend swatch, a day chip's
 * corner marker) and is the saturated colour the tier is actually called. Only the two graded tiers
 * carry a `bg`, a `bar` or a `marker`; neutral returns ink alone.
 */
export type GapToneStyle = { bg?: string; ink: string; bar?: string; marker?: string };

/**
 * Rank the shortfalls of one shift row: the worst distinct magnitude → `critical`, the second → `short`,
 * everything else → `neutral`.
 *
 * Ranking is over DISTINCT magnitudes, so a row whose two worst days are both short by 6 shows two
 * criticals rather than arbitrarily promoting one of them — "the highest missing value" is a value, not
 * a single cell.
 *
 * `missing` is the gap in people — positive = short, negative = surplus, 0 = balanced.
 */
export function buildGapToneFn(missingValues: Iterable<number>): (missing: number) => GapTone {
	const shortfalls = [...new Set([...missingValues].filter((missing) => missing > 0))].sort((a, b) => b - a);
	const worst = shortfalls[0];
	const second = shortfalls[1];
	return (missing: number): GapTone => {
		if (missing <= 0) {
			return "neutral";
		}
		if (worst !== undefined && missing >= worst) {
			return "critical";
		}
		if (second !== undefined && missing >= second) {
			return "short";
		}
		return "neutral";
	};
}

/**
 * The one place a tone becomes colour, so the table cell, the legend chip above it and the still-short
 * dot on a suggested hire's day chip can never disagree — "if the cell is yellow, the dot is yellow"
 * holds by construction, and a legend that stopped describing the thing it sits over is not a state
 * this module can be put into.
 *
 * `bg` comes from `GAP_TONE`, the Hiring Module's OWN pair of gap fills, and deliberately not from
 * `COVERAGE.*_BG`: that scale is shared with the master-schedule coverage grids, so warming these
 * fills for the hiring design there would restyle a feature nobody asked to change. The ink, bar and
 * marker ARE the shared coverage ones — those carry the tier's severity and read the same everywhere.
 * `marker` is the saturated counterpart for text-free dots — see `COVERAGE.*_MARKER`.
 */
export function gapToneStyle(tone: GapTone): GapToneStyle {
	if (tone === "critical") {
		return { bg: GAP_TONE.CRITICAL_BG, ink: COVERAGE.CRITICAL_INK, bar: COVERAGE.CRITICAL_BAR, marker: COVERAGE.CRITICAL_MARKER };
	}
	if (tone === "short") {
		return { bg: GAP_TONE.WARNING_BG, ink: COVERAGE.WARNING_INK, bar: COVERAGE.WARNING_BAR, marker: COVERAGE.WARNING_MARKER };
	}
	// No marker: an ungraded day has nothing to point at, and the absence is what the day chips read to
	// decide whether to render a corner marker at all — the token decides, not a `=== "neutral"` test.
	return { ink: INK.MUTED };
}

const TONE_RANK: Record<GapTone, number> = { neutral: 0, short: 1, critical: 2 };

/** The most severe tone in a set — a day chip covering several shift types wears its worst one. */
export function worstGapTone(tones: Iterable<GapTone>): GapTone {
	let worst: GapTone = "neutral";
	for (const tone of tones) {
		if (TONE_RANK[tone] > TONE_RANK[worst]) {
			worst = tone;
		}
	}
	return worst;
}
